import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import useIsMobile from '../../../../shared/hooks/useIsMobile';
import SwapPropDropdown from './SwapPropDropdown';
import AddPropertyColumnComponent from './AddPropertyColumnComponent';
import Text from '../../../../shared/components/Text/Text';
import {
  saveCalendarExercise,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
  setPreSaveTableLocalState,
  saveNonDateBasedWorkoutExercise,
} from '../../ducks/calendarActions';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import { normalizeRepArray } from '../../../../shared/utils/normalizeRepToken';
import wipeRepKeywordOnMutation from '../../../../shared/utils/wipeRepKeywordOnMutation';
import { useEnterSavesForm } from './hooks/useEnterSavesForm';

// Accepts: 55, 55.5, 55-60, 55.5-60.8, 5+, 4.5+
const VALUE_PATTERN = /^\d+(\.\d+)?(-\d+(\.\d+)?|\+)?$/;
const MAX_FIELD_VALUE = 10000;
const isValidRangeToken = (tok) => {
  if (!VALUE_PATTERN.test(tok)) return false;
  const parts = tok.replace('+', '').split('-');
  if (!parts.every((p) => parseFloat(p) <= MAX_FIELD_VALUE)) return false;
  if (parts.length === 2 && parseFloat(parts[0]) > parseFloat(parts[1])) return false;
  return true;
};
// For submit: pure numbers go as floats; range/+ tokens go as strings (backend accepts both)
const toFieldValue = (token) => {
  if (/^\d+(\.\d+)?$/.test(token)) return parseFloat(token);
  return token;
};

const cleanPercentPRList = (raw) => raw
  .split(',')
  .map((token) => {
    const trimmed = token.trim();
    if (trimmed === '') return token;
    const n = parseFloat(trimmed);
    if (Number.isFinite(n) && n >= 0) {
      const leading = token.match(/^\s*/)[0];
      const trailing = token.match(/\s*$/)[0];
      return `${leading}${Math.round(n)}${trailing}`;
    }
    return token.replace(/[^\d\s]/g, '');
  })
  .join(',');

const FormWrapper = styled('form')`
  display: flex;
  width: 100%;
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: scroll;
`;

const PrimaryRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  margin-top: ${(props) => (props.isAdding ? '16px' : '0px')};
`;

const ValueRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const PrimaryRowValue = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  max-height: ${(props) => (props.isAdding && '24px')};
  margin-bottom: 3px;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: ${(props) => (!props.isError ? `1px solid ${props.theme.colors.borderLight}` : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};
  }
`;

const SetRowValue = styled('input')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  text-align: center;
  border-radius: 4px;
`;

const SaveButtonContainer = styled('button')`
  display: flex;
  background: #10cd8c;
  height: 34px;
  padding-left: 8px;
  padding-right: 8px;
  font-family: 'Nunito Sans';
  color: ${(props) => (props.shimmer ? 'transparent' : props.theme.colors.white)};
  border-radius: 5px;
  font-weight: 700;
  font-size: 13px;
  align-items: center;
  min-width: 56px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  width: 100%;
`;

const RemoveIcon = styled('div')`
  padding: 4px 4px 4px 8px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RowsContainer = styled('div')`
  height: 40px;
  display: flex;
  width: 100%;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: ${(props) => (props.hasError ? '12px' : '0px')};
`;

const ErrorAndRowsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const SetRowValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InfoIconWrapper = styled('div')`
  position: absolute;
  left: -20px;
  display: flex;
  align-items: center;
`;

const PrimaryValueContainer = styled('div')`
  flex: 1;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const SetPrimaryValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const InputWithErrorWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

const InputError = styled('div')`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -2px;
  font-size: 11px;
  font-family: 'Nunito Sans';
  color: red;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

const ShimmerSetRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  margin-right: 5px;
`;

const SaveButtonWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
  padding-top: 18px;
  margin-top: 32px;
`;

const SaveButtonContainerText = styled('div')`
  margin-top: 2px;
  font-weight: 700;
  font-family: 'Nunito Sans';
`;

const PreSaveTable = ({
  completionOnly,
  coachCompletion,
  isLeftAndRight,
  contentIndex,
  content,
  trackRepCount,
  trackVolumeLoad,
  localAdditionalInfo,
  swapDropdownSelectedExercise,
  forcePrUpdate,
  disablePrUpdate,
  measure,
  date,
  dayNumber,
  setCoachCompletion,
  setCompletionOnly,
  setIsLeftAndRight,
  setTrackVolumeLoad,
  setTrackRepCount,
  setForcePrUpdate,
  setDisablePrUpdate,
}) => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);

  /** Enter anywhere in this table saves it, exactly as clicking SAVE does —
   * blurring the focused cell first so its onBlur normalization runs. Stands
   * down inside month view's popover, which owns Enter there. */
  useEnterSavesForm(tableContainerRef);
  const [setNumberValue, setSetNumberValue] = useState(1);
  const [percentPR, setPercentPR] = useState('');
  const [isBodyweight, setIsBodyweight] = useState(!!content?.defaultOptions?.liftRepsOnly);
  const [isTime, setIsTime] = useState(false);
  const [errors, setErrors] = useState({
    reps: null,
    load: null,
    percentPR: null,
  });
  const setFieldError = useCallback((field, msg) => {
    setErrors((prev) => ({ ...prev, [field]: msg }));
  }, []);
  const errorTooltipId = `lifts-presave-error-tooltip-${content.assignedId}`;
  const [tempo, setTempo] = useState(null);
  const [rest, setRest] = useState(null);
  const [load, setLoad] = useState(null);
  const [loadInputValue, setLoadInputValue] = useState(''); // Track the raw load input string
  const [isAdding, setIsAdding] = useState(false);
  const [peakPowerEnum, setPeakPowerEnum] = useState(1);
  const [repsInputValue, setRepsInputValue] = useState(''); // Track the raw reps input string

  // Generate unique IDs for tooltips
  const repsTooltipId = `reps-tooltip-${content.assignedId}`;
  const timeTooltipId = `time-tooltip-${content.assignedId}`;
  const peakPowerTooltipId = `peak-power-tooltip-${content.assignedId}`;
  const velocityTooltipId = `velocity-tooltip-${content.assignedId}`;

  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  const isMobile = useIsMobile();
  const preSaveTableLocalStateForItem = useSelector(
    (state) => state.calendar.data.preSaveTableLocalState[content.assignedId],
  );

  const [exerciseData, setExerciseData] = useState({
    reps: [],
    load: null,
    rest: null,
    tempo: null,
    velocity: null,
    peakPower: null,
  });

  const [primaryRows, setPrimaryRows] = useState([
    { name: 'set' },
    { name: isTime ? 'time' : 'reps' },
    { name: isBodyweight ? 'bodyweight' : 'load' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'tempo' },
    { name: 'velocity' },
    { name: 'peak power', label: 'Peak Power (w)' },
    { name: '%pr' },
    { name: 'add-property', label: 'Add Property' },
  ]);

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState({
    set: true,
    reps: true,
    load: false,
    bodyweight: !!content.defaultOptions?.liftRepsOnly,
    rest: !!content?.defaultOptions?.liftRestTime,
    tempo: false,
    velocity: !!content?.defaultOptions?.barSpeed,
    'peak power': !!content?.defaultOptions?.peakPower,
    '%pr': false,
    'add-property': true,
  });

  const setPrimaryRowSwitchState = (newState) => {
    setPrimaryRowSwitch(newState);
  };

  const setTempoFunction = (value) => {
    setTempo(value);
  };

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
  };

  const setPercentPRFunction = (rawValue) => {
    const value = cleanPercentPRList(rawValue);
    setPercentPR(value);

    if (!value) {
      setFieldError('percentPR', null);
      return;
    }

    const prArray = value.split(',').map((pr) => pr.trim()).filter(Boolean);
    const setCount = parseInt(setNumberValue, 10);

    const invalidValues = [];
    prArray.forEach((pr, index) => {
      if (!/^\d+$/.test(pr)) {
        invalidValues.push({ value: pr, position: index + 1 });
      }
    });

    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('percentPR', `Invalid %PR format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use whole numbers only.`);
      } else {
        const list = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('percentPR', `Invalid %PR format for values: ${list}. Use whole numbers only.`);
      }
      return;
    }

    if (prArray.length !== setCount && prArray.length !== 1 && prArray.length > 0) {
      setFieldError('percentPR', 'Enter a %PR value to apply to all sets.');
    } else {
      setFieldError('percentPR', null);
    }
  };

  /** This useEffect detects if the user chooses a different exercise
   * from the item dropdownn select and updates the pre saved item in redux
   * so that the table structure and content updates to match the selected exercise
   */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise.name,
        exerciseId: parseInt(swapDropdownSelectedExercise.id, 10),
        id: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        assignedId: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        preSave: true,
        type: swapDropdownSelectedExercise?.type || 'T',
      };
      if (programType === 'free-form-program') {
        dispatch(
          updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex),
        );
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
      }
    }
  }, [swapDropdownSelectedExercise]);

  const addPropertyInitialized = useRef(false);

  // Turn off 'add-property' when all optional properties are enabled
  useEffect(() => {
    // Skip on initial mount to allow backup restoration to complete
    if (!addPropertyInitialized.current) {
      addPropertyInitialized.current = true;
      return;
    }

    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
      && primaryRowSwitch.tempo
      && primaryRowSwitch.velocity
      && primaryRowSwitch['peak power']
      && primaryRowSwitch['%pr']
      && primaryRowSwitch.load;

    if (allOptionalPropertiesEnabled && primaryRowSwitch['add-property']) {
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': false,
      }));
    } else if (!allOptionalPropertiesEnabled && !primaryRowSwitch['add-property']) {
      // Re-enable add-property if any optional property is turned off
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': true,
      }));
    }
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch.tempo,
    primaryRowSwitch.velocity,
    primaryRowSwitch['peak power'],
    primaryRowSwitch['%pr'],
    primaryRowSwitch.load,
  ]);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      /** skip first run because cell measurer wrapper
      * will get that and we don't want to run it twice */
      return;
    }
    if (measure) {
      measure();
    }
  }, [errors]);

  useEffect(() => {
    if (isBodyweight) {
      setLoad(null);
      setLoadInputValue('');
    }
  }, [isBodyweight]);

  // When toggling reps↔time: swap AMAP↔ALAP tokens in the value (if present),
  // then re-run validation against the new keyword — without wiping user input.
  useEffect(() => {
    if (!repsInputValue) return;
    const fromKeyword = isTime ? 'AMAP' : 'ALAP';
    const toKeyword = isTime ? 'ALAP' : 'AMAP';
    const swapped = repsInputValue
      .split(',')
      .map((tok) => {
        const trimmed = tok.trim();
        const lead = tok.slice(0, tok.length - tok.trimStart().length);
        const trail = tok.slice(tok.trimEnd().length);
        if (trimmed.toUpperCase() === fromKeyword) return `${lead}${toKeyword}${trail}`;
        return tok;
      })
      .join(',');
    if (swapped !== repsInputValue) setRepsInputValue(swapped);
    // eslint-disable-next-line no-use-before-define
    handleInputChange(isTime ? 'time' : 'reps', swapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTime]);

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element
  // with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this
  // input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (!isMobile && focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [isMobile, focusTarget, content.assignedId]);

  // Initialize state from Redux backup if it exists
  const backupState = preSaveTableLocalStateForItem;
  const hasRestoredBackupRef = useRef(null);

  useEffect(() => {
    // Only restore once per assignedId — ref guard prevents re-execution
    // when auto-save writes back to the same backup slice
    if (hasRestoredBackupRef.current === content.assignedId) return;
    if (!backupState) return;

    hasRestoredBackupRef.current = content.assignedId;

    if (backupState.setNumberValue !== undefined) setSetNumberValue(backupState.setNumberValue);
    if (backupState.percentPR !== undefined) {
      const normalized = String(backupState.percentPR)
        .split(',')
        .map((tok) => {
          const trimmed = tok.trim();
          if (!trimmed) return trimmed;
          const n = parseFloat(trimmed);
          return Number.isNaN(n) ? trimmed : String(Math.round(n));
        })
        .join(', ');
      setPercentPR(normalized);
    }
    if (backupState.isBodyweight !== undefined) setIsBodyweight(backupState.isBodyweight);
    if (backupState.isTime !== undefined) setIsTime(backupState.isTime);
    const shouldNormalizeToAlap = !!backupState.isTime;
    const toAlap = (s) => (typeof s === 'string' && s.toUpperCase() === 'AMAP' ? 'ALAP' : s);
    if (backupState.tempo !== undefined) setTempo(backupState.tempo);
    if (backupState.rest !== undefined) setRest(backupState.rest);
    if (backupState.load !== undefined) setLoad(backupState.load);
    if (backupState.loadInputValue !== undefined) setLoadInputValue(backupState.loadInputValue);
    if (backupState.peakPowerEnum !== undefined) setPeakPowerEnum(backupState.peakPowerEnum);
    if (backupState.repsInputValue !== undefined) {
      setRepsInputValue(
        shouldNormalizeToAlap
          ? backupState.repsInputValue.split(',').map((v) => toAlap(v.trim())).join(', ')
          : backupState.repsInputValue,
      );
    }
    if (backupState.exerciseData !== undefined) {
      setExerciseData(
        shouldNormalizeToAlap && Array.isArray(backupState.exerciseData?.reps)
          ? { ...backupState.exerciseData, reps: backupState.exerciseData.reps.map(toAlap) }
          : backupState.exerciseData,
      );
    }
    if (backupState.primaryRowSwitch !== undefined) {
      setPrimaryRowSwitch(backupState.primaryRowSwitch);
    }
    if (backupState.primaryRows !== undefined) setPrimaryRows(backupState.primaryRows);
    if (backupState.coachCompletion !== undefined) setCoachCompletion(backupState.coachCompletion);
    if (backupState.completionOnly !== undefined) setCompletionOnly(backupState.completionOnly);
    if (backupState.isLeftAndRight !== undefined) setIsLeftAndRight(backupState.isLeftAndRight);
    if (backupState.trackVolumeLoad !== undefined) setTrackVolumeLoad(backupState.trackVolumeLoad);
    if (backupState.trackRepCount !== undefined) setTrackRepCount(backupState.trackRepCount);
    if (backupState.forcePrUpdate !== undefined) setForcePrUpdate(backupState.forcePrUpdate);
    if (backupState.disablePrUpdate !== undefined) setDisablePrUpdate(backupState.disablePrUpdate);
  }, [
    content.assignedId,
    backupState,
    setCoachCompletion,
    setCompletionOnly,
    setIsLeftAndRight,
    setTrackVolumeLoad,
    setTrackRepCount,
    setForcePrUpdate,
    setDisablePrUpdate,
  ]);

  // Save current state to Redux backup on blur
  const saveStateToRedux = useCallback(() => {
    const stateObject = {
      setNumberValue,
      percentPR,
      isBodyweight,
      isTime,
      tempo,
      rest,
      load,
      loadInputValue,
      peakPowerEnum,
      repsInputValue,
      exerciseData,
      primaryRowSwitch,
      primaryRows,
      coachCompletion,
      completionOnly,
      isLeftAndRight,
      trackVolumeLoad,
      trackRepCount,
      forcePrUpdate,
      disablePrUpdate,
    };
    dispatch(setPreSaveTableLocalState(content.assignedId, stateObject));
  }, [
    setNumberValue, percentPR, isBodyweight, isTime, tempo, rest, load,
    loadInputValue, peakPowerEnum, repsInputValue, exerciseData, primaryRowSwitch, primaryRows,
    coachCompletion, completionOnly, isLeftAndRight, trackVolumeLoad,
    trackRepCount, forcePrUpdate, disablePrUpdate,
    content.assignedId, dispatch,
  ]);

  const autoSaveInitialized = useRef(false);
  const { colors } = useTheme();

  // Auto-save state to Redux on every change (not just blur)
  // This ensures state is preserved when component re-renders (e.g., superset selection)
  useEffect(() => {
    // Skip the initial render cycle - only save after user makes changes
    if (!autoSaveInitialized.current) {
      autoSaveInitialized.current = true;
      return;
    }
    saveStateToRedux();
  }, [saveStateToRedux]);

  const handleInputChange = (field, value) => {
    if (field === 'reps' || field === 'time') {
      setRepsInputValue(value);

      const keyword = field === 'time' ? 'ALAP' : 'AMAP';
      const fieldLabel = field === 'time' ? 'time' : 'reps';

      // Check for partial "custom" input
      const trimmedValue = value.trim().toLowerCase();
      if (['c', 'cu', 'cus', 'cust'].includes(trimmedValue)) {
        setFieldError('reps', 'If you would like to program multiple sets, please enter them as comma separated values, e.g. 12,10,8 to match the number of sets');
        return;
      }

      const repsArray = value
        .split(',')
        .map((rep) => rep.trim())
        .filter(Boolean);

      const setCount = parseInt(setNumberValue, 10);
      const {
        isValid: allValid,
        values: normalizedReps,
        invalidValues,
      } = normalizeRepArray(repsArray, [keyword]);

      setExerciseData((prev) => ({
        ...prev,
        reps: normalizedReps,
      }));

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        }
        return;
      }

      if (repsArray.length !== setCount && repsArray.length !== 1 && repsArray.length > 0) {
        setFieldError('reps', `Enter a number for ${fieldLabel} to apply to all sets.`);
      } else {
        setFieldError('reps', null);
      }
    } else if (field === 'sets') {
      // Handle sets field change and re-validate reps
      setSetNumberValue(value);

      // Re-validate existing reps against new set count
      if (exerciseData.reps && exerciseData.reps.length > 0) {
        const newSetCount = parseInt(value, 10);
        const repsArray = exerciseData.reps;

        // Check if reps count matches new set count
        if (repsArray.length !== newSetCount && repsArray.length !== 1 && repsArray.length > 0) {
          setFieldError('reps', 'Enter a number for reps to apply to all sets.');
        } else {
          setFieldError('reps', null);
        }
      }

      // Re-validate existing load values against new set count
      if (loadInputValue && loadInputValue.trim() !== '') {
        const newSetCount = parseInt(value, 10);
        const loadArray = loadInputValue
          .split(',')
          .map((loadVal) => loadVal.trim())
          .filter(Boolean);

        // Check if load count matches new set count
        if (loadArray.length !== newSetCount && loadArray.length !== 1 && loadArray.length > 0) {
          setFieldError('load', 'Enter a number for load to apply to all sets.');
        } else {
          setFieldError('load', null);
        }
      }
    } else {
      // Update other fields normally
      setExerciseData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleRepsBlur = () => {
    const keyword = isTime ? 'ALAP' : 'AMAP';
    if (Array.isArray(exerciseData.reps)) {
      const setCount = parseInt(setNumberValue, 10);

      const {
        isValid: allValid,
        values: normalizedTokens,
        invalidValues,
      } = normalizeRepArray(exerciseData.reps, [keyword]);

      if (!allValid) {
        if (invalidValues.length === 1) {
          setFieldError('reps', `Invalid format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('reps', `Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${keyword}".`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (normalizedTokens.length === 1) {
        const filledReps = Array(setCount).fill(normalizedTokens[0]);
        setExerciseData((prev) => ({
          ...prev,
          reps: filledReps,
        }));
        // Update the input display to show the normalized version
        setRepsInputValue(filledReps.join(', '));
        setFieldError('reps', null);
      } else if (normalizedTokens.length === setCount) {
        setExerciseData((prev) => ({
          ...prev,
          reps: normalizedTokens,
        }));
        setRepsInputValue(normalizedTokens.join(', '));
        setFieldError('reps', null);
      } else {
        setFieldError('reps', `Expected ${setCount} values or 1 value (to apply to all sets), but have ${normalizedTokens.length} values.`);
      }
    } else {
      setFieldError('reps', `Invalid input type. Expected comma-separated values but received: ${typeof exerciseData.reps}.`);
    }
  };

  const handleRepsKeyDown = (e) => {
    const { key, target } = e;
    const { value, selectionStart, selectionEnd } = target;
    const keyword = isTime ? 'ALAP' : 'AMAP';
    const field = isTime ? 'time' : 'reps';

    // If the user is mutating an AMAP/ALAP token they're sitting in the middle
    // of, block the keystroke and restore — except Backspace, which deletes
    // the whole token (opt-in via backspaceDeletes).
    const wipeResult = wipeRepKeywordOnMutation(e, value, selectionStart, selectionEnd, [keyword], { backspaceDeletes: true });
    if (wipeResult.wiped) {
      e.preventDefault();
      setRepsInputValue(wipeResult.newValue);
      handleInputChange(field, wipeResult.newValue);
      requestAnimationFrame(() => {
        target.setSelectionRange(wipeResult.newCursor, wipeResult.newCursor);
      });
      return;
    }

    // Handle 'a' or 'A' key press for keyword auto-expansion
    if (key.toLowerCase() === 'a') {
      const beforeCursor = value.substring(0, selectionStart);
      const isAtValueStart = beforeCursor === '' || beforeCursor.endsWith(', ') || beforeCursor.endsWith(',');

      if (isAtValueStart) {
        e.preventDefault();
        const beforeSelection = value.substring(0, selectionStart);
        const afterSelection = value.substring(selectionEnd);
        const newValue = `${beforeSelection}${keyword}${afterSelection}`;
        const newCursorPosition = selectionStart + keyword.length;

        setRepsInputValue(newValue);
        handleInputChange(field, newValue);

        requestAnimationFrame(() => {
          target.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    }
  };

  const handlePercentPRBlur = () => {
    if (percentPR) {
      const cleaned = cleanPercentPRList(percentPR);
      if (cleaned !== percentPR) {
        setPercentPR(cleaned);
      }
      const prArray = cleaned
        .split(',')
        .map((pr) => pr.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);

      // Check if all %PR values are valid and collect invalid ones
      const invalidValues = [];
      prArray.forEach((pr, index) => {
        const isValid = /^\d+$/.test(pr); // Only whole numbers allowed
        if (!isValid) {
          invalidValues.push({ value: pr, position: index + 1 });
        }
      });

      // If any %PR is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('percentPR', `Invalid %PR format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use whole numbers only.`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('percentPR', `Invalid %PR format for values: ${invalidList}. Use whole numbers only.`);
        }
        return;
      }

      // Check if the number of %PR values matches the set count
      if (prArray.length !== setCount && prArray.length !== 1) {
        setFieldError('percentPR', `Expected ${setCount} %PR values or 1 value (to apply to all sets), but have ${prArray.length} values.`);
      } else {
        setFieldError('percentPR', null);
      }
    } else {
      setFieldError('percentPR', null);
    }
  };

  const handleLoadInputChange = (value) => {
    setLoadInputValue(value);

    // For load, store the raw array and validate without auto-normalization
    const loadArray = value
      .split(',')
      .map((loadVal) => loadVal.trim())
      .filter(Boolean);

    // Validate format but don't auto-normalize
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];

    // Check each value for valid format (numbers, ranges, or plus notation)
    loadArray.forEach((loadVal, index) => {
      if (!isValidRangeToken(loadVal)) {
        invalidValues.push({ value: loadVal, position: index + 1 });
      }
    });

    // Show format errors
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setFieldError('load', `Invalid load format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      } else {
        const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setFieldError('load', `Invalid load format for values: ${invalidList}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
      }
      return;
    }

    // Show count mismatch errors (but don't auto-normalize)
    if (loadArray.length !== setCount && loadArray.length !== 1 && loadArray.length > 0) {
      setFieldError('load', 'Enter a load value to apply to all sets.');
    } else {
      setFieldError('load', null);
    }
  };

  const handleLoadBlur = () => {
    if (loadInputValue && loadInputValue.trim() !== '') {
      const loadArray = loadInputValue
        .split(',')
        .map((loadVal) => loadVal.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);

      // Check if all load values are valid and collect invalid ones
      const invalidValues = [];
      loadArray.forEach((loadVal, index) => {
        if (!isValidRangeToken(loadVal)) {
          invalidValues.push({ value: loadVal, position: index + 1 });
        }
      });

      // If any load is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setFieldError('load', `Invalid load format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setFieldError('load', `Invalid load format for values: ${invalidList}. Use a number, range (e.g. 4-6), or plus (e.g. 5+).`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (loadArray.length === 1) {
        const normalizedLoad = Array(setCount).fill(loadArray[0]);
        setLoadInputValue(normalizedLoad.join(', '));
        setFieldError('load', null);
      } else if (loadArray.length === setCount) {
        setFieldError('load', null);
      } else {
        setFieldError('load', `Expected ${setCount} load values or 1 value (to apply to all sets), but have ${loadArray.length} values.`);
      }
    } else {
      setFieldError('load', null);
    }
  };

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const errorAddReset = () => {
    setIsAdding(false);
  };

  /**
 * Handles the saving of a calendar exercise by preparing and dispatching the required data.
 *
 * @param {number} contentIndexParam - Index of the content item being saved.
 */
  const handleSaveCalendarExercise = (contentIndexParam) => {
    // Check if reps field is empty when reps are enabled
    if (primaryRowSwitch.reps && (!exerciseData.reps || exerciseData.reps.length === 0 || (exerciseData.reps.length === 1 && exerciseData.reps[0] === ''))) {
      showErrorToast('Reps field cannot be empty');
      return;
    }

    // Defense-in-depth: re-validate and normalize reps right before submission so no
    // case-drifted ("Amap") or invalid ("AMRAP") tokens can leak past the errors gate.
    const submissionKeyword = isTime ? 'ALAP' : 'AMAP';
    const repsForValidation = Array.isArray(exerciseData.reps) ? exerciseData.reps : [];
    const { isValid: repsAreValid, values: submissionReps } = normalizeRepArray(
      repsForValidation,
      [submissionKeyword],
    );
    if (primaryRowSwitch.reps && !repsAreValid) {
      showErrorToast(`Invalid reps value. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "${submissionKeyword}".`);
      return;
    }

    // Proceed only if there are no errors.
    if (!Object.values(errors).some(Boolean)) {
    // Indicate that the saving process has started.
      setIsAdding(true);

      // Parse load values if present
      let loadArray = [];
      if (loadInputValue && loadInputValue.trim() !== '' && !isBodyweight) {
        loadArray = loadInputValue
          .split(',')
          .map((loadVal) => loadVal.trim())
          .filter(Boolean);
      }

      // Generate an array representing the details for each set.
      const setSubmissionArray = Array.from({ length: setNumberValue }, (_, i) => {
        const setData = {
          setNumber: i + 1,
          reps: submissionReps[i]
          ?? submissionReps[0],
          // Use specific reps for the set, defaulting to the first value if undefined.
        };

        // Handle load mapping to individual sets
        if (loadArray.length > 0) {
          setData.prescribedValue = toFieldValue(loadArray[i]
            ?? loadArray[0]); // Use specific load for the set, defaulting to first value
        }

        // Handle %PR mapping to individual sets
        if (percentPR) {
          const prArray = percentPR
            .split(',')
            .map((pr) => pr.trim())
            .filter(Boolean);
          if (prArray.length > 0) {
            setData.percent = prArray[i]
            ?? prArray[0]; // Use specific %PR for the set, defaulting to first value
          }
        }

        return setData;
      });

      // Create the main submission object with exercise details.
      const submissionObject = {
        coachCompletion, // Indicates if the coach marks this exercise as complete.
        completionOnly, // If true, only completion is tracked.
        completionRequired: true, // Ensure completion is required for this exercise.
        eachSide: isLeftAndRight, // Flag for tracking each side of the body separately.
        trackRepCount, // Whether to track the number of reps.
        trackVolumeLoad, // Whether to track volume load.
        forceMaxUpdate: forcePrUpdate,
        maxTrackingDisabled: disablePrUpdate,
        repIntensityUnit: null, // Reserved for rep intensity unit, currently null.
        type: content.type === 'T' ? 'tag' : typeFormatter(content.typeDescription), // Exercise type derived from a formatter.
        sets: setSubmissionArray, // Array of set details.
        showSessionBreakItems: false, // Whether session break items are shown (disabled here).
        repsOnly: isBodyweight, // Indicates if this is a bodyweight-only exercise.
        repType: isTime ? 'time' : 'reps', // Specifies whether the exercise tracks time or reps.
        groupingLetter: content.groupingLetter,
        /** Conditional key based on programType: "date" OR "dayNumber" */
        [programType === 'free-form-program' ? 'dayNumber' : 'date']:
        programType === 'free-form-program'
          ? dayNumber
          : moment(effectiveDate).format('YYYY-MM-DD'),
      };

      if (content.type === 'T') {
        submissionObject.tagId = content.exerciseId;
      } else {
        submissionObject.exerciseId = content.exerciseId;
      }

      // Optional properties based on exercise settings.
      if (tempo && primaryRowSwitch.tempo) {
        submissionObject.tempo = tempo; // Add tempo if applicable.
      }
      if (content.description) {
        submissionObject.description = content.description;
        // Include exercise description if provided.
      }
      if (peakPowerEnum && primaryRowSwitch['peak power']) {
        submissionObject.trackPeakPower = peakPowerEnum; // Add peak power tracking if enabled.
      }
      if (rest && primaryRowSwitch.rest) {
        submissionObject.restTime = rest; // Add rest time between sets if specified.
      }
      if (primaryRowSwitch.velocity) {
        submissionObject.trackBarSpeed = primaryRowSwitch.velocity; // Track bar speed if enabled.
      }
      if (localAdditionalInfo) {
        submissionObject.additionalInfo = localAdditionalInfo; // Include any additional local info.
      }

      // Format the date for dispatching.
      const formattedDate = moment(date).format('YYYY-MM-DD');

      // Dispatch the save action with the submission object.
      if (programType === 'free-form-program') {
        dispatch(saveNonDateBasedWorkoutExercise(
          currentUser, // Current user performing the save.
          contentIndexParam, // Index of the content item.
          submissionObject, // Submission details.
          dayNumber, // program 0 indexed dayNumber.
          errorAddReset, // Error handling/reset callback.
          {
            onSuccess: () => {
            // Trigger focus to next preSave item
              dispatch(focusNextPreSaveProgramItem(contentIndexParam, dayNumber));
            },
          },
          content.assignedId,
        ));
      } else {
        dispatch(saveCalendarExercise(
          currentUser, // Current user performing the save.
          contentIndexParam, // Index of the content item.
          submissionObject, // Submission details.
          formattedDate, // Formatted date.
          errorAddReset, // Error handling/reset callback.
          {
            onSuccess: () => {
            // Trigger focus to next preSave item
              dispatch(focusNextPreSaveItem(contentIndexParam, effectiveDate));
            },
          },
          content.assignedId,
        ));
      }
    }
  };

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'tempo') {
      return (
        <RowValueContainer>
          <RowValue
            value={tempo}
            onChange={(e) => setTempoFunction(e.target.value)}
            onBlur={saveStateToRedux}
            background={colors.surfaceSecondary}
          />
        </RowValueContainer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            onBlur={saveStateToRedux}
            background={colors.surfaceSecondary}
          />
        </RowValueContainer>
      );
    }
    if (name === '%pr') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              isError={!!errors.percentPR}
              type='text'
              value={percentPR}
              onChange={(e) => setPercentPRFunction(e.target.value)}
              background={colors.surfaceSecondary}
              onBlur={() => {
                handlePercentPRBlur();
                saveStateToRedux();
              }}
            />
            {errors.percentPR && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.percentPR}
              >
                {errors.percentPR}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'load' || name === 'bodyweight') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              isError={name === 'load' && !!errors.load && !isBodyweight}
              type='text'
              value={isBodyweight ? '' : loadInputValue}
              onChange={(e) => handleLoadInputChange(e.target.value)}
              onBlur={() => {
                handleLoadBlur();
                saveStateToRedux();
              }}
              background={colors.surfaceSecondary}
              placeholder={name === 'bodyweight' ? '-' : null}
              textColor={name === 'bodyweight' ? colors.textTertiary : colors.text}
              disabled={isBodyweight}
            />
            {name === 'load' && errors.load && !isBodyweight && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.load}
              >
                {errors.load}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'peak power') {
      if (peakPowerEnum === 1) {
        return (
          <RowValueContainer>
            <RowValue
              disabled
              placeholder='-'
              textColor={colors.textPlaceholder}
              background={colors.surfaceSecondary}
            />
          </RowValueContainer>
        );
      }
      return (
        <RowValueContainer>
          <RowValue
            disabled
            placeholder='Left'
            textColor={colors.textPlaceholder}
            style={{
              marginRight: 8,
            }}
            background={colors.surfaceSecondary}
          />
          <RowValue
            disabled
            placeholder='Right'
            textColor={colors.textPlaceholder}
            style={{
              marginLeft: 8,
            }}
            background={colors.surfaceSecondary}
          />
        </RowValueContainer>
      );
    }
    if (name === 'velocity') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            disabled
            placeholder='-'
            textColor={colors.textPlaceholder}
          />
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <InputWithErrorWrapper>
          <RowValue
            isError={(name === 'reps' || name === 'time') && !!errors.reps}
            type='text'
            background={colors.surfaceSecondary}
            value={(name === 'reps' || name === 'time') ? repsInputValue : undefined}
            onChange={(e) => handleInputChange(name, e.target.value)}
            onBlur={(name === 'reps' || name === 'time') ? () => {
              handleRepsBlur();
              saveStateToRedux();
            } : saveStateToRedux}
            onKeyDown={(name === 'reps' || name === 'time') ? handleRepsKeyDown : null}
            data-focus-target={
              // Add focus target to the first editable
              // field that's visible (set field takes priority)
              (name === 'reps' && primaryRowSwitch.reps && !primaryRowSwitch.set)
              || (name === 'time' && primaryRowSwitch.time && !primaryRowSwitch.set && !primaryRowSwitch.reps)
              || (name === 'load' && primaryRowSwitch.load && !primaryRowSwitch.set && !primaryRowSwitch.reps && !primaryRowSwitch.time)
                ? content.assignedId
                : undefined
            }
          />
          {(name === 'reps' || name === 'time') && errors.reps && (
            <InputError
              data-tooltip-id={errorTooltipId}
              data-tooltip-content={errors.reps}
            >
              {errors.reps}
            </InputError>
          )}
        </InputWithErrorWrapper>
      </RowValueContainer>
    );
  };

  const updateColumnName = useCallback((oldName, newName) => {
    // Prevent updating the add-property column
    if (oldName === 'add-property' || newName === 'add-property') return;

    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      if (unitContent?.unitAbbr === 'wgt') {
        // orgWeight: 1 = kg, 0 = lbs
        const weightUnit = currentUser?.userSettings?.orgWeight === 1 ? 'kg' : 'lbs';
        return `(${weightUnit})`;
      }
      return `(${unitContent?.unitAbbr})`;
    }
    return '';
  };

  const getTooltipId = (primaryRowName) => {
    if (primaryRowName === 'reps') return repsTooltipId;
    if (primaryRowName === 'time') return timeTooltipId;
    if (primaryRowName === 'peak power') return peakPowerTooltipId;
    if (primaryRowName === 'velocity') return velocityTooltipId;
    return undefined;
  };

  const renderPrimaryRowValue = (primaryRow) => {
    if (!primaryRowSwitch[primaryRow.name]) {
      return null;
    }

    // Handle 'set' column
    if (primaryRow.name === 'set') {
      if (!isAdding) {
        return (
          <SetRowValueContainer>
            <SetRowValue
              data-focus-target={primaryRowSwitch.set ? content.assignedId : undefined}
              type='number'
              value={setNumberValue}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                // Prevent negative values
                if (value < 0) return;
                handleInputChange('sets', value);
              }}
              onBlur={saveStateToRedux}
            />
          </SetRowValueContainer>
        );
      }
      return (
        <Shimmer>
          <SetRowValueContainer shimmer>
            <ShimmerSetRowValue />
          </SetRowValueContainer>
        </Shimmer>
      );
    }

    // Handle 'add-property' column
    if (primaryRow.name === 'add-property') {
      if (!isAdding) {
        return (
          <RowValueContainer>
            <AddPropertyColumnComponent
              mode='input'
              primaryRows={primaryRows}
              primaryRowSwitch={primaryRowSwitch}
              setPrimaryRowSwitch={setPrimaryRowSwitch}
              setPrimaryRowSwitchState={setPrimaryRowSwitchState}
              assignedId={content.assignedId}
            />
          </RowValueContainer>
        );
      }
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }

    // Handle other columns
    return rowValueDecider(primaryRow.name, isAdding);
  };

  const renderPrimaryRowHeader = (primaryRow) => {
    if (!primaryRowSwitch[primaryRow.name]) {
      return null;
    }

    // Handle 'set' column
    if (primaryRow.name === 'set') {
      if (!isAdding) {
        return (
          <SetPrimaryValueContainer>
            {`${capitalizeWords(primaryRow.name)}s`}
          </SetPrimaryValueContainer>
        );
      }
      return (
        <Shimmer>
          <SetPrimaryValueContainer shimmer>
            {capitalizeWords(primaryRow.name)}
          </SetPrimaryValueContainer>
        </Shimmer>
      );
    }

    // Handle 'add-property' column
    if (primaryRow.name === 'add-property') {
      if (!isAdding) {
        return (
          <PrimaryValueContainer>
            <AddPropertyColumnComponent
              mode='header'
              primaryRows={primaryRows}
              primaryRowSwitch={primaryRowSwitch}
              setPrimaryRowSwitch={setPrimaryRowSwitch}
              setPrimaryRowSwitchState={setPrimaryRowSwitchState}
              assignedId={content.assignedId}
            />
          </PrimaryValueContainer>
        );
      }
      return (
        <Shimmer>
          <PrimaryValueContainer shimmer>
            <PrimaryRowValue isAdding={isAdding}>
              Add Property
            </PrimaryRowValue>
          </PrimaryValueContainer>
        </Shimmer>
      );
    }

    // Handle other columns
    if (!isAdding) {
      return (
        <PrimaryValueContainer>
          <PrimaryRowValue isAdding={isAdding}>
            {primaryRow.name === 'reps' || primaryRow.name === 'time' || primaryRow.name === 'velocity' || primaryRow.name === 'peak power' ? (
              <NameAndDropdown>
                {(primaryRow.name === 'peak power' || primaryRow.name === 'reps' || primaryRow.name === 'time' || primaryRow.name === 'velocity') && (
                  <InfoIconWrapper>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={14}
                      icon='info2'
                      color={colors.textSecondary}
                      style={{ cursor: 'pointer' }}
                      data-tooltip-id={getTooltipId(primaryRow.name)}
                    />
                  </InfoIconWrapper>
                )}

                <SwapPropDropdown
                  setPrimaryRowSwitch={setPrimaryRowSwitch}
                  primaryRowSwitch={primaryRowSwitch}
                  rowName={primaryRow.name}
                  size={12}
                  updateColumnName={updateColumnName}
                  name={primaryRow.name === 'load' ? capitalizeWords('Load') : primaryRow.label || capitalizeWords(primaryRow.name)}
                  unit={primaryRow.name === 'load' ? findCorrectUnit(content) : null}
                  peakPowerEnum={peakPowerEnum}
                  setPeakPowerEnum={setPeakPowerEnum}
                  setIsBodyweight={setIsBodyweight}
                  setIsTime={setIsTime}
                />
              </NameAndDropdown>
            ) : (
              <NameAndDropdown>
                <SwapPropDropdown
                  setPrimaryRowSwitch={setPrimaryRowSwitch}
                  primaryRowSwitch={primaryRowSwitch}
                  rowName={primaryRow.name}
                  size={12}
                  updateColumnName={updateColumnName}
                  name={primaryRow.name === 'load' ? capitalizeWords('Load') : primaryRow.label || capitalizeWords(primaryRow.name)}
                  unit={primaryRow.name === 'load' ? findCorrectUnit(content) : null}
                  peakPowerEnum={peakPowerEnum}
                  setPeakPowerEnum={setPeakPowerEnum}
                  setIsBodyweight={setIsBodyweight}
                  setIsTime={setIsTime}
                />
              </NameAndDropdown>
            )}
          </PrimaryRowValue>
        </PrimaryValueContainer>
      );
    }

    return (
      <Shimmer>
        <PrimaryValueContainer shimmer>
          <PrimaryRowValue isAdding={isAdding}>
            <NameAndDropdown>
              {primaryRow.name === 'load' ? capitalizeWords('Load') : capitalizeWords(primaryRow.name)}
              {' '}
              {primaryRow.name === 'load' ? findCorrectUnit(content) : null}
            </NameAndDropdown>
          </PrimaryRowValue>
        </PrimaryValueContainer>
      </Shimmer>
    );
  };

  return (
    <FormWrapper
      ref={tableContainerRef}
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveCalendarExercise(contentIndex);
      }}
    >
      <Container>
        <PrimaryRow isAdding={isAdding}>
          {primaryRows.map((primaryRow) => renderPrimaryRowHeader(primaryRow))}
        </PrimaryRow>
        <ValueRow>
          <ErrorAndRowsContainer>
            <RowsContainer hasError={Object.values(errors).some(Boolean)}>
              {primaryRows.map((primaryRow) => renderPrimaryRowValue(primaryRow))}
            </RowsContainer>
          </ErrorAndRowsContainer>
        </ValueRow>
      </Container>
      <SaveButtonWrapper>
        {!isAdding ? (
          <SaveButtonContainer type='submit'>
            <SaveButtonContainerText>
              ADD
            </SaveButtonContainerText>
            <RemoveIcon>
              <IcomoonReact
                iconSet={iconSet}
                size={12}
                icon='down-arrow'
                color={colors.white}
              />
            </RemoveIcon>
          </SaveButtonContainer>
        ) : (
          <Shimmer>
            <SaveButtonContainer shimmer>
              <SaveButtonContainerText>
                ADD
              </SaveButtonContainerText>
              <RemoveIcon>
                <IcomoonReact
                  iconSet={iconSet}
                  size={12}
                  icon='down-arrow'
                  color='transparent'
                />
              </RemoveIcon>
            </SaveButtonContainer>
          </Shimmer>
        )}
      </SaveButtonWrapper>

      {/* ReactTooltip components */}
      <ReactTooltip
        id={repsTooltipId}
        place='top'
        positionStrategy='fixed'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '250px',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color={colors.white}>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>
              <Text fontSize='13px' fontWeight={400} color={colors.white}>Absolute (e.g. 5)</Text>
            </li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>Comma Separated (e.g. 12,10,8)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>Range (e.g. 8-12)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>Minimum (e.g. 8+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>&quot;AMAP&quot; for as many as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={timeTooltipId}
        place='top'
        positionStrategy='fixed'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '250px',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color={colors.white}>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>
              <Text fontSize='13px' fontWeight={400} color={colors.white}>Absolute (e.g. 30)</Text>
            </li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>Comma Separated (e.g. 30,20,10)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>Range (e.g. 30-60)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>Minimum (e.g. 30+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color={colors.white}>&quot;ALAP&quot; for as long as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={peakPowerTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Athlete input only'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />

      <ReactTooltip
        id={velocityTooltipId}
        place='top'
        positionStrategy='fixed'
        content='Athlete input only'
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />
      <ReactTooltip
        id={errorTooltipId}
        place='top'
        positionStrategy='fixed'
        delayShow={300}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '12px',
          maxWidth: '260px',
          padding: '6px 10px',
          lineHeight: 1.3,
        }}
      />
    </FormWrapper>
  );
};

PreSaveTable.propTypes = {
  coachCompletion: PropTypes.bool.isRequired,
  trackVolumeLoad: PropTypes.bool.isRequired,
  forcePrUpdate: PropTypes.bool.isRequired,
  disablePrUpdate: PropTypes.bool.isRequired,
  isLeftAndRight: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  trackRepCount: PropTypes.bool.isRequired,
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string.isRequired,
  measure: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  setCoachCompletion: PropTypes.func.isRequired,
  setCompletionOnly: PropTypes.func.isRequired,
  setIsLeftAndRight: PropTypes.func.isRequired,
  setTrackVolumeLoad: PropTypes.func.isRequired,
  setTrackRepCount: PropTypes.func.isRequired,
  setForcePrUpdate: PropTypes.func.isRequired,
  setDisablePrUpdate: PropTypes.func.isRequired,
};

PreSaveTable.defaultProps = {
  date: null,
  dayNumber: null,
};

export default PreSaveTable;
